/**
 * RGB color tuple type
 */
type RGB = [number, number, number];

/**
 * Calculates a color on a gradient between two colors based on a value between 0 and 1
 * @param value - A number between 0 and 1 (will be clamped if outside this range)
 * @param startColor - The color at value 0, either as a hex string (e.g., "#FF0000") or RGB array [r, g, b]
 * @param endColor - The color at value 1, either as a hex string (e.g., "#00FF00") or RGB array [r, g, b]
 * @returns The color as a hex string
 */
export function getGradientColor(
  value: number,
  startColor: string | RGB = "#FF0000",
  endColor: string | RGB = "#00FF00",
): string {
  // Ensure value is between 0 and 1
  const clampedValue = Math.max(0, Math.min(1, value));

  // Convert input colors to RGB arrays if they're hex strings
  const startRGB = typeof startColor === "string" ? hexToRgb(startColor) : startColor;
  const endRGB = typeof endColor === "string" ? hexToRgb(endColor) : endColor;

  // Interpolate between the colors
  const resultRGB: RGB = [
    Math.round(startRGB[0] + (endRGB[0] - startRGB[0]) * clampedValue),
    Math.round(startRGB[1] + (endRGB[1] - startRGB[1]) * clampedValue),
    Math.round(startRGB[2] + (endRGB[2] - startRGB[2]) * clampedValue),
  ];

  // Convert back to hex
  return rgbToHex(resultRGB);
}

/**
 * Converts a hex color string to an RGB array
 * @param hex - Color in hex format (e.g., "#FF0000" or "#F00")
 * @returns RGB color as [r, g, b] with values from 0-255
 */
function hexToRgb(hex: string): RGB {
  // Remove # if present
  hex = hex.replace(/^#/, "");

  // Expand shorthand form (e.g., "F00" to "FF0000")
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map(char => char + char)
      .join("");
  }

  // Parse the hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return [r, g, b];
}

/**
 * Converts an RGB array to a hex color string
 * @param rgb - RGB color as [r, g, b] with values from 0-255
 * @returns Color in hex format (e.g., "#FF0000")
 */
function rgbToHex(rgb: RGB): string {
  return (
    "#" +
    rgb
      .map(value => {
        const hex = Math.max(0, Math.min(255, value)).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
}

export default {
  getGradientColor,
};
